// SPDX-License-Identifier: AGPL-3.0-or-later

/** How long to wait after detecting a web update before force-reloading. */
export const WEB_UPDATE_FORCE_RELOAD_MS = 3 * 60 * 1000;

/**
 * After a gateway disconnect lasting at least this long, recheck `/version.json` on reconnect.
 * Short blips stay on the normal 30-minute poll + focus/visibility checks.
 */
export const WEB_UPDATE_RECONNECT_CHECK_AFTER_MS = 3_000;

/**
 * Web update checks compare `/version.json` to the baked-in build version.
 * Skip when the client has no real version (local `pnpm dev` bakes `"dev"`).
 */
export function isWebUpdateCheckEnabled(buildVersion: string | null | undefined): boolean {
	if (buildVersion == null) {
		return false;
	}
	const normalized = buildVersion.trim();
	return normalized.length > 0 && normalized !== 'dev';
}

/** True when reconnect follows a disconnect long enough to warrant a forced version check. */
export function shouldCheckWebUpdateAfterReconnect(params: {
	readonly disconnectedAt: number | null;
	readonly reconnectedAt: number;
	readonly minDisconnectMs?: number;
}): boolean {
	if (params.disconnectedAt == null) {
		return false;
	}
	const minMs = params.minDisconnectMs ?? WEB_UPDATE_RECONNECT_CHECK_AFTER_MS;
	return params.reconnectedAt - params.disconnectedAt >= minMs;
}

export function isWebUpdateAvailable(params: {
	readonly buildVersion: string | null | undefined;
	readonly remoteVersion: string | null | undefined;
}): boolean {
	if (!isWebUpdateCheckEnabled(params.buildVersion)) {
		return false;
	}
	const remote = params.remoteVersion?.trim();
	if (!remote) {
		return false;
	}
	return remote !== params.buildVersion!.trim();
}

/** Whole minutes/seconds remaining until a force reload, for localized countdown labels. */
export function getWebUpdateCountdownParts(remainingMs: number): {
	readonly minutes: number;
	readonly seconds: number;
	readonly totalSeconds: number;
} {
	const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
	return {
		minutes: Math.floor(totalSeconds / 60),
		seconds: totalSeconds % 60,
		totalSeconds,
	};
}
